import React from "react";
import {
  VictoryAxis,
  VictoryChart,
  VictoryLine,
  VictoryScatter,
  VictoryTheme,
  VictoryTooltip,
} from "victory";

interface ChartDataPoint {
  x: Date | number;
  y: number;
}

type TimeRange = "1d" | "7d" | "1m" | "6m" | "1y";

interface BodyTemperatureChartProps {
  selectedRange: TimeRange;
  navigationOffset: number;
  filteredData: {
    date: string;
    value: number;
    hour?: number;
    datetime?: string;
  }[];
  chartData: ChartDataPoint[];
  loading: boolean;
  error: string | null;
  metadata: {
    avg: number;
    min: number;
    max: number;
  } | null;
  chartConfig: {
    scale: { x: "time" | "linear" };
    domain: { x: [Date, Date] | [number, number]; y: [number, number] };
    tickValues: (Date | number)[];
    tickFormat: (x: Date | number) => string;
  };
}

export const BodyTemperatureChart: React.FC<BodyTemperatureChartProps> = ({
  selectedRange,
  navigationOffset,
  filteredData,
  chartData,
  loading,
  error,
  metadata,
  chartConfig,
}) => {
  // Handle tickFormat logic internally
  const getTickFormat = (x: Date | number) => {
    if (typeof x === "number") return String(x);
    if (x instanceof Date) {
      // For year view, show month names, for other views show weekdays
      if (selectedRange === "1y" || selectedRange === "6m") {
        return x.toLocaleDateString("en-US", { month: "short" });
      } else {
        // Use local date formatting instead of UTC
        return x.toLocaleDateString("en-US", { weekday: "short" });
      }
    }
    return String(x);
  };

  const finalChartConfig = {
    ...chartConfig,
    tickFormat: chartConfig.tickFormat ?? getTickFormat,
  };

  if (loading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="text-sm text-gray-400">Loading chart data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="text-sm text-red-400">Failed to load chart data</div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Chart with data */}
      {filteredData.length > 0 && (
        <VictoryChart
          key={`chart-with-data-${selectedRange}`}
          theme={VictoryTheme.material}
          width={400}
          height={200}
          style={{ parent: { maxWidth: "100%" } }}
          padding={{ left: 35, right: 35, top: 10, bottom: 30 }}
          domain={finalChartConfig.domain}
          scale={finalChartConfig.scale}
        >
          {/* Y Axis */}
          <VictoryAxis
            dependentAxis
            domain={finalChartConfig.domain.y}
            style={{
              grid: {
                stroke: "rgba(107, 114, 128, 0.3)",
                strokeWidth: 1,
              },
              axis: { stroke: "none" },
              tickLabels: {
                fill: "#9CA3AF",
                fontSize: 6,
                fontFamily: "inherit",
              },
              ticks: { stroke: "none" },
            }}
          />

          {/* X Axis */}
          <VictoryAxis
            tickValues={finalChartConfig.tickValues}
            tickFormat={finalChartConfig.tickFormat}
            style={{
              grid: { stroke: "none" },
              axis: { stroke: "rgba(107, 114, 128, 0.3)", strokeWidth: 1 },
              tickLabels: {
                fill: "#9CA3AF",
                fontSize: 6,
                fontFamily: "inherit",
              },
              ticks: { stroke: "rgba(107, 114, 128, 0.3)", size: 5 },
            }}
            fixLabelOverlap={false}
          />

          {/* Body Temperature Line through scatter points */}
          <VictoryLine
            data={chartData}
            style={{
              data: {
                stroke: "#ffc6c6",
                strokeWidth: 2,
                strokeLinecap: "round",
              },
            }}
            animate={{
              duration: 300,
              easing: "sinIn",
              onLoad: { duration: 400 },
              delay: -10,
            }}
            interpolation="linear"
          />

          {/* Average Line (straight line) */}
          {filteredData.length > 0 && metadata?.avg && (
            <VictoryLine
              data={[
                { x: finalChartConfig.domain.x[0], y: metadata.avg },
                { x: finalChartConfig.domain.x[1], y: metadata.avg },
              ]}
              style={{
                data: {
                  stroke: "#10B981",
                  strokeWidth: 1,
                  strokeDasharray: "5,5",
                },
              }}
              animate={{
                duration: 300,
                onLoad: { duration: 400 },
                delay: -10,
                easing: "sinIn",
              }}
            />
          )}

          {/* Body Temperature Scatter Points */}
          <VictoryScatter
            data={chartData.map((point, index) => {
              const timeLabel = generateTooltipTimeLabel(
                selectedRange,
                index,
                filteredData,
                navigationOffset,
              );

              return {
                ...point,
                label: createTooltipLabel(timeLabel, point.y),
              };
            })}
            size={2}
            style={{
              data: {
                fill: "#ffc6c6",
                stroke: "#1F2937",
                strokeWidth: 0.5,
              },
            }}
            labelComponent={
              <VictoryTooltip
                style={{
                  fill: "#FFFFFF",
                  fontSize: 5,
                  fontFamily: "inherit",
                }}
                flyoutStyle={{
                  fill: "#1F2937",
                  stroke: "#ffc6c6",
                  strokeWidth: 0.5,
                }}
                renderInPortal={true}
                pointerLength={8}
                cornerRadius={4}
              />
            }
          />
        </VictoryChart>
      )}

      {/* Chart without data - only axes */}
      {filteredData.length === 0 && (
        <VictoryChart
          key={`chart-without-data-${selectedRange}`}
          theme={VictoryTheme.material}
          width={400}
          height={200}
          style={{ parent: { maxWidth: "100%" } }}
          padding={{ left: 35, right: 35, top: 10, bottom: 30 }}
          domain={finalChartConfig.domain}
          scale={finalChartConfig.scale}
        >
          {/* Y Axis */}
          <VictoryAxis
            dependentAxis
            domain={finalChartConfig.domain.y}
            style={{
              grid: {
                stroke: "rgba(107, 114, 128, 0.3)",
                strokeWidth: 1,
              },
              axis: { stroke: "none" },
              tickLabels: {
                fill: "#9CA3AF",
                fontSize: 6,
                fontFamily: "inherit",
              },
              ticks: { stroke: "none" },
            }}
          />

          {/* X Axis */}
          <VictoryAxis
            tickValues={finalChartConfig.tickValues}
            tickFormat={finalChartConfig.tickFormat}
            style={{
              grid: { stroke: "none" },
              axis: { stroke: "rgba(107, 114, 128, 0.3)", strokeWidth: 1 },
              tickLabels: {
                fill: "#9CA3AF",
                fontSize: 6,
                fontFamily: "inherit",
              },
              ticks: { stroke: "rgba(107, 114, 128, 0.3)", size: 5 },
            }}
            fixLabelOverlap={false}
          />
        </VictoryChart>
      )}

      {/* No data message */}
      {filteredData.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-sm text-gray-400">No data available</div>
        </div>
      )}
    </div>
  );
};

// Helper function to generate tooltip time label
const generateTooltipTimeLabel = (
  selectedRange: TimeRange,
  index: number,
  filteredData: {
    date: string;
    value: number;
    hour?: number;
    datetime?: string;
  }[],
  _navigationOffset: number,
): string => {
  if (index >= filteredData.length) return "";

  const item = filteredData[index];
  const date = new Date(item.date);

  switch (selectedRange) {
    case "1d":
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    case "7d":
      return date.toLocaleDateString("en-US", { weekday: "short" });
    case "1m":
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    case "6m":
      return date.toLocaleDateString("en-US", { month: "short" });
    case "1y":
      return date.toLocaleDateString("en-US", { month: "short" });
    default:
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
  }
};

// Helper function to create tooltip label
const createTooltipLabel = (timeLabel: string, value: number): string => {
  return `${timeLabel}\n${value}°F`;
};
